import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

const HardwareBackContext = createContext(null);

/**
 * Lets nested workflow UIs register temporary hardware-Back handlers.
 * Handlers run newest-first; return true when the event is consumed.
 */
export function HardwareBackProvider({ children }) {
  const handlersRef = useRef([]);

  const registerHandler = useCallback((handler) => {
    handlersRef.current.push(handler);
    return () => {
      handlersRef.current = handlersRef.current.filter((item) => item !== handler);
    };
  }, []);

  const consumeRegisteredBack = useCallback(() => {
    const handlers = [...handlersRef.current].reverse();
    for (const handler of handlers) {
      if (handler()) {
        return true;
      }
    }
    return false;
  }, []);

  const value = useMemo(
    () => ({
      registerHandler,
      consumeRegisteredBack,
    }),
    [consumeRegisteredBack, registerHandler],
  );

  return (
    <HardwareBackContext.Provider value={value}>
      {children}
    </HardwareBackContext.Provider>
  );
}

export function useHardwareBackContext() {
  const context = useContext(HardwareBackContext);
  if (!context) {
    throw new Error("useHardwareBackContext must be used within HardwareBackProvider");
  }
  return context;
}

/**
 * @param {() => boolean} handler Return true when Back is handled.
 * @param {boolean} [enabled=true]
 */
export function useHardwareBackHandler(handler, enabled = true) {
  const context = React.useContext(HardwareBackContext);

  React.useEffect(() => {
    if (!enabled || !context) {
      return undefined;
    }
    return context.registerHandler(handler);
  }, [enabled, handler, context]);
}
